import React, { useState, useCallback } from "react";
import { PreorderMenu } from "../../../../../../types/VenueGroup";
import { VenuePreorderMenu } from "../../../../../../types/venues";
import { EDSButton } from "../../../../../eds/EDSButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../../ui/select";
import { Plus } from "lucide-react";

// Union type for preorder menus from venue or venue group
type PreorderMenuUnion = PreorderMenu | VenuePreorderMenu;

interface AddMenuDropdownProps {
  availableMenus: PreorderMenuUnion[];
  onAddMenu: (menuId: string) => Promise<void>;
  isSaving: boolean;
}

const AddMenuDropdown: React.FC<AddMenuDropdownProps> = ({
  availableMenus,
  onAddMenu,
  isSaving
}) => {
  const [selectedMenuId, setSelectedMenuId] = useState<string>("");

  const handleAddMenu = useCallback(async () => {
    if (!selectedMenuId) return;

    await onAddMenu(selectedMenuId);
    setSelectedMenuId("");
  }, [selectedMenuId, onAddMenu]);

  const renderSelectContent = () => {
    if (availableMenus.length === 0) {
      return (
        <div className="p-2 text-sm text-muted-foreground">
          No menus available
        </div>
      );
    }

    return availableMenus.map((menu) => (
      <SelectItem key={menu.id} value={menu.id}>
        {menu.name}
      </SelectItem>
    ));
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={selectedMenuId}
        onValueChange={setSelectedMenuId}
        disabled={isSaving}
      >
        <SelectTrigger className="flex-1" aria-label="Select a menu to add">
          <SelectValue placeholder="Select a menu to add..." />
        </SelectTrigger>
        <SelectContent>{renderSelectContent()}</SelectContent>
      </Select>
      <EDSButton
        variant="default"
        size="sm"
        onClick={handleAddMenu}
        disabled={!selectedMenuId || isSaving}
        aria-label="Add selected menu"
      >
        <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
        Add Menu
      </EDSButton>
    </div>
  );
};

export default AddMenuDropdown;
